import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Alert,
  Button,
  Drawer,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Switch,
  Typography,
  message,
} from 'antd';
import { CheckOutlined, CodeOutlined } from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';
import type { ModelRow } from '@/api/models';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// Add / edit one ai.AIModel row.
//
// Every field carries the CONSEQUENCE of the choice, not a restatement of the
// column name — because the two capability flags here can each break a call
// silently, and "SupportsTemperature" tells an operator nothing on its own.
//
// The endpoint picker is the important one: ApiId chooses the provider, which
// chooses whether a clinical prompt leaves the Azure tenant.
//
// Rates are NOT edited here. They live in ai.AiModelPricing (append-only, and
// ai.PromptLog points at the exact row used at call time) and are managed from
// the card's rate strip, which opens the pricing drawer.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  /** null = create mode. */
  editing: ModelRow | null;
  onClose: () => void;
}

interface FormShape {
  name: string;
  title?: string;
  deploymentName?: string;
  developmentCompanyId: number;
  apiId: number;
  merchantId?: number | null;
  active: boolean;
  enableInChat: boolean;
  enableInProcess: boolean;
  maxContextTokens?: number | null;
  supportsVision: boolean;
  supportsReasoningEffort: boolean;
  supportsTemperature: boolean;
  reasoningEffortLevel?: string | null;
  temperature?: number | null;
  topP?: number | null;
  toolDescriptionMaxLength?: number | null;
  defaultProcessAssistantId?: string | null;
}

export const ModelEditorDrawer = observer(function ModelEditorDrawer({ open, editing, onClose }: Props) {
  const { modelsStore: store, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [form] = Form.useForm<FormShape>();
  const [submitting, setSubmitting] = useState(false);

  const isEdit = editing != null;

  // Watched so the inline warnings react as the operator toggles, rather than
  // only after a save round-trip.
  const supportsReasoning = Form.useWatch('supportsReasoningEffort', form);
  const reasoningLevel = Form.useWatch('reasoningEffortLevel', form);
  const supportsTemperature = Form.useWatch('supportsTemperature', form);
  const temperature = Form.useWatch('temperature', form);
  const apiId = Form.useWatch('apiId', form);

  const chosenApi = store.endpoints.find((e) => e.id === apiId);

  useEffect(() => {
    if (!open) return;
    void store.fetchLookups();

    if (editing) {
      form.setFieldsValue({
        name: editing.name,
        title: editing.title ?? undefined,
        deploymentName: editing.deploymentName ?? undefined,
        developmentCompanyId: editing.developmentCompanyId,
        apiId: editing.apiId,
        merchantId: editing.merchantId ?? null,
        active: editing.active,
        enableInChat: editing.enableInChat,
        enableInProcess: editing.enableInProcess,
        maxContextTokens: editing.maxContextTokens ?? null,
        supportsVision: editing.supportsVision,
        supportsReasoningEffort: editing.supportsReasoningEffort,
        supportsTemperature: editing.supportsTemperature,
        reasoningEffortLevel: editing.reasoningEffortLevel ?? null,
        temperature: editing.temperature ?? null,
        topP: editing.topP ?? null,
        toolDescriptionMaxLength: editing.toolDescriptionMaxLength ?? null,
        defaultProcessAssistantId: editing.defaultProcessAssistantId ?? null,
      });
    } else {
      form.setFieldsValue({
        name: '',
        developmentCompanyId: undefined as unknown as number,
        apiId: undefined as unknown as number,
        merchantId: null,
        active: false,
        enableInChat: true,
        enableInProcess: true,
        maxContextTokens: null,
        supportsVision: true,
        supportsReasoningEffort: false,
        supportsTemperature: true,
        reasoningEffortLevel: null,
        temperature: null,
        topP: null,
        toolDescriptionMaxLength: null,
        defaultProcessAssistantId: null,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?.id]);

  async function handleSubmit(): Promise<void> {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      const payload: Partial<ModelRow> & { id?: number } = {
        ...(isEdit && editing ? { id: editing.id } : {}),
        name: values.name.trim(),
        title: values.title?.trim() || values.name.trim(),
        deploymentName: values.deploymentName?.trim() || null,
        developmentCompanyId: values.developmentCompanyId,
        apiId: values.apiId,
        merchantId: values.merchantId ?? null,
        active: values.active,
        enableInChat: values.enableInChat,
        enableInProcess: values.enableInProcess,
        maxContextTokens: values.maxContextTokens ?? null,
        supportsVision: values.supportsVision,
        supportsReasoningEffort: values.supportsReasoningEffort,
        supportsTemperature: values.supportsTemperature,
        reasoningEffortLevel: values.reasoningEffortLevel || null,
        temperature: values.temperature ?? null,
        topP: values.topP ?? null,
        toolDescriptionMaxLength: values.toolDescriptionMaxLength ?? null,
        defaultProcessAssistantId: values.defaultProcessAssistantId?.trim() || null,
      };

      if (isEdit) {
        await store.update(payload as Partial<ModelRow> & { id: number });
        message.success('Model updated');
        trackEvent('Models.Updated', { id: editing!.id });
      } else {
        await store.create(payload);
        message.success('Model added');
        trackEvent('Models.Added', { name: payload.name });
      }
      form.resetFields();
      onClose();
    } catch (err: unknown) {
      const isFormError = err && typeof err === 'object' && 'errorFields' in err;
      if (!isFormError) message.error(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Drawer
      open={open}
      onClose={() => {
        form.resetFields();
        onClose();
      }}
      width={620}
      destroyOnClose
      styles={{ body: { padding: '20px 22px' } }}
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <CodeOutlined /> {isEdit ? `Edit ${editing!.name}` : 'New model deployment'}
        </span>
      }
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" loading={submitting} onClick={handleSubmit} icon={<CheckOutlined />}>
              {isEdit ? 'Save changes' : 'Add model'}
            </Button>
          </Space>
        </div>
      }
    >
      <Form form={form} layout="vertical" preserve={false} requiredMark="optional">
        <SectionLabel isDark={isDark}>Identity</SectionLabel>

        <Form.Item
          name="name"
          label="Model name"
          extra="Must match what the provider reports, because ai.PromptLog.ModelName is the key cost lookup uses. Examples: gpt-4.1, gpt-5.4-1, claude-opus-4-7-1"
          rules={[{ required: true, message: 'Model name is required' }]}
        >
          <Input placeholder="gpt-5.4-1" style={{ fontFamily: "'JetBrains Mono', monospace" }} />
        </Form.Item>

        <Form.Item
          name="deploymentName"
          label="Deployment slug (optional)"
          extra="The name the deployment was provisioned under, when it differs from the model name."
        >
          <Input placeholder="gpt-4o-2" style={{ fontFamily: "'JetBrains Mono', monospace" }} />
        </Form.Item>

        <Form.Item name="title" label="Display title (optional)" extra="Shown in pickers. Defaults to the model name.">
          <Input placeholder="GPT-5.4 (Azure AI Foundry)" />
        </Form.Item>

        <Form.Item
          name="developmentCompanyId"
          label="Vendor"
          extra="Who makes the model. Used to group the catalog and to pick the right rate."
          rules={[{ required: true, message: 'Vendor is required' }]}
        >
          <Select
            placeholder="Select a vendor"
            options={store.providers.map((p) => ({ value: p.id, label: p.name }))}
          />
        </Form.Item>

        <SectionLabel isDark={isDark}>Where calls go</SectionLabel>

        <Form.Item
          name="apiId"
          label="Endpoint"
          extra="Chooses the provider and the API shape for every call this row serves."
          rules={[{ required: true, message: 'Endpoint is required' }]}
        >
          <Select
            placeholder="Select an endpoint"
            options={store.endpoints.map((e) => ({
              value: e.id,
              label: `${e.title} — ${e.host}`,
            }))}
          />
        </Form.Item>

        {chosenApi && !chosenApi.isAzureHosted && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="This endpoint is outside the Azure tenant"
            description={
              <span style={{ fontSize: 12.5 }}>
                Prompts served by this row go to <code>{chosenApi.host}</code>. Confirm that is intended
                for the agents you will wire here before treating the row as clinical-ready.
              </span>
            }
          />
        )}

        <Form.Item
          name="merchantId"
          label="Scope"
          extra="Leave blank for a platform default every tenant inherits. Set a merchant id to scope the row to one practice — other tenants will then need their own row."
        >
          <InputNumber placeholder="Every tenant" style={{ width: '100%' }} min={0} />
        </Form.Item>

        <SectionLabel isDark={isDark}>Availability</SectionLabel>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <Form.Item label="Active" name="active" valuePropName="checked" extra="Offer it in pickers.">
            <Switch />
          </Form.Item>
          <Form.Item label="Chat" name="enableInChat" valuePropName="checked" extra="Usable by chat.">
            <Switch />
          </Form.Item>
          <Form.Item label="Workflows" name="enableInProcess" valuePropName="checked" extra="Usable by workflow nodes.">
            <Switch />
          </Form.Item>
        </div>

        <SectionLabel isDark={isDark}>Capabilities</SectionLabel>

        <Form.Item
          name="maxContextTokens"
          label="Context window (tokens)"
          extra="Leave blank only if genuinely unknown: the compactor then assumes 128,000 and this model is excluded from context-overload alerting entirely."
        >
          <InputNumber style={{ width: '100%' }} min={1} step={1000} placeholder="1047576" />
        </Form.Item>

        <Form.Item
          label="Accepts image input"
          name="supportsVision"
          valuePropName="checked"
          extra="When off, an attached image forces a switch to a vision-capable model — and fails if the tenant has none."
        >
          <Switch />
        </Form.Item>

        <Form.Item
          label="Accepts reasoning_effort"
          name="supportsReasoningEffort"
          valuePropName="checked"
          extra="Turn on ONLY for models that genuinely take the parameter (o-series, reasoning GPT-5 variants). Sending it to one that does not returns HTTP 400 and breaks the call."
        >
          <Switch />
        </Form.Item>

        <Form.Item
          name="reasoningEffortLevel"
          label="Default reasoning level"
          extra="Applies only when the capability above is on."
        >
          <Select
            allowClear
            placeholder="None"
            options={[
              { value: 'low', label: 'low' },
              { value: 'medium', label: 'medium' },
              { value: 'high', label: 'high' },
            ]}
          />
        </Form.Item>

        {!supportsReasoning && reasoningLevel && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={`“${reasoningLevel}” will never be applied`}
            description={
              <span style={{ fontSize: 12.5 }}>
                The capability above is off, so the reasoning level is gated out before the call is built.
                Either turn it on or clear the level, so the row stops advertising a setting it cannot honour.
              </span>
            }
          />
        )}

        <Form.Item
          label="Accepts temperature"
          name="supportsTemperature"
          valuePropName="checked"
          extra="Off for Claude on Foundry, which returns HTTP 400 if temperature is sent at all — even the SDK default."
        >
          <Switch />
        </Form.Item>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item name="temperature" label="Temperature" extra="Lower keeps wording predictable.">
            <InputNumber style={{ width: '100%' }} min={0} max={2} step={0.1} placeholder="0.2" />
          </Form.Item>
          <Form.Item name="topP" label="Top-p">
            <InputNumber style={{ width: '100%' }} min={0} max={1} step={0.1} placeholder="1.0" />
          </Form.Item>
        </div>

        {!supportsTemperature && temperature != null && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={`Temperature ${temperature} is stripped from every call`}
            description={
              <span style={{ fontSize: 12.5 }}>
                With the capability off, the parameter is removed before the request is sent, so the stored
                number is decoration. Clear it so the row reads honestly.
              </span>
            }
          />
        )}

        <Form.Item
          name="toolDescriptionMaxLength"
          label="Tool description cap (characters)"
          extra="Set when the provider rejects long tool descriptions — Claude on Foundry caps each at 1024. Blank means no cap."
        >
          <InputNumber style={{ width: '100%' }} min={1} step={64} placeholder="1024" />
        </Form.Item>

        <SectionLabel isDark={isDark}>Workflow conversation</SectionLabel>

        <Form.Item
          name="defaultProcessAssistantId"
          label="Default process assistant id (optional)"
          extra="Copied onto the conversation options for every workflow node that runs on this model."
        >
          <Input placeholder="asst_…" style={{ fontFamily: "'JetBrains Mono', monospace" }} />
        </Form.Item>

        <Text type="secondary" style={{ fontSize: 11.5, display: 'block', marginTop: 8 }}>
          Rates are not edited here — they live in <code>ai.AiModelPricing</code> and are managed from the
          price strip on the model card, so historical cost keeps its audit trail.
        </Text>
      </Form>
    </Drawer>
  );
});

function SectionLabel({ children, isDark }: { children: React.ReactNode; isDark: boolean }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: isDark ? colors.slate[400] : colors.slate[500],
        marginBottom: 12,
        marginTop: 6,
      }}
    >
      {children}
    </div>
  );
}
